import type { ShortCode } from "@/features/sub-families/types";
import type { Locale, LooseAutocomplete, SiteEnum } from "@/types";
import EventQueue from "../store/event-queue.ts";
import { userGaveConsent } from "../utils/one-trust.ts";
import { pushTrackingData } from "../utils/push-event.ts";

export const TrackingEvent = {
    SmartNumberCopy: "3d-copy-to-clipboard",
    TabNavigation: "3d-nav-tab",
    TabNavigationSummary: "3d-nav-summary",
    NavigationTechnical: "3d-nav-technical",
    NavigationProjects: "3d-nav-projects",
    TechnicalDownload: "3d-technical",
    ContactForm: "3d-contactform",
    ConfiguratorStart: "3d-configurator-start",
} as const;

export type GlobalEventFields = "site" | "locale";
type EventData<T> = { site: SiteEnum; locale: Locale; component: Component } & T;
type Component = LooseAutocomplete<"header" | "summary" | "navigation" | "modal">;
type Action = LooseAutocomplete<"open" | "submit">;

export type EventMap = {
    [TrackingEvent.SmartNumberCopy]: EventData<{ smartnumber?: string; shortCode?: ShortCode }>;
    [TrackingEvent.TabNavigation]: EventData<{ model_code: string; label: string }>;
    [TrackingEvent.TabNavigationSummary]: EventData<{ model_code: string; label: string; smartNumber: string; event: "configurator_submit" }>; // prettier-ignore
    [TrackingEvent.NavigationTechnical]: EventData<{ model_code: string; label: string }>;
    [TrackingEvent.NavigationProjects]: EventData<{ model_code: string; label: string }>;
    [TrackingEvent.TechnicalDownload]: EventData<{ model_code: string; label: string }>;
    [TrackingEvent.ContactForm]: EventData<{ model_code: string; action: Action; event?: "contact_form" }>;
    [TrackingEvent.ConfiguratorStart]: { event: "configurator_start" };
};

export function trackEvent<E extends keyof EventMap>(
    event: E,
    ...args: EventMap[E] extends never ? [] : [data: EventMap[E]]
) {
    const [data] = args;

    const trackingObject = {
        ...data,
        name: event,
        // When we specifically set an event in the data object, we use that. otherwise fallback to the event name
        event: !!data && "event" in data ? data.event : event,
    };

    if (!userGaveConsent()) {
        const eventQueue = EventQueue.getInstance();
        eventQueue.push(trackingObject);
    } else {
        pushTrackingData(trackingObject);
    }
}
